/**
 * Admin-recorded investment closings, and the investor's countersignature.
 *
 * Deals are agreed off platform. An admin records the result, the investor signs from
 * their OWN session, and the admin confirms the wire landed. Three actors, three steps,
 * and the split is deliberate: a signature captured in the issuer's session is weak
 * evidence of the investor's assent, and an investment activated on signature alone would
 * be paid out by settlement whether or not any money arrived.
 */

import { api } from './apiClient';

export type ClosingStatus =
  | 'pending_signature'
  | 'pending_payment'
  | 'active'
  | 'cancelled'
  | 'paid_out'
  | 'refunded';

export interface RecordedInvestment {
  id: string;
  contractNumber: string;
  status: ClosingStatus;
  shares: number;
  amountInvestedCents: number;
  platformEntryFeeCents: number;
  createdAt: string;
  contractSignedAt: string | null;
  fundsReceivedAt: string | null;
  closingNotes: string | null;
  hasContract: boolean;
  investor: { userId: string; email: string; name: string | null };
  recordedBy: string | null;
  opportunity: { id: string; title: string; offeringType: string };
  /** Left behind by the removed self-serve flow. No closing action applies to it. */
  isLegacy: boolean;
  canCountersign: boolean;
  canConfirmFunds: boolean;
  canCancel: boolean;
}

export interface RecordableInvestor {
  userId: string;
  email: string;
  name: string | null;
  accreditationExpiresAt: string | null;
  /** How many non-cancelled holdings they already have in this raise. */
  existingInRaise: number;
}

export const listRecordedInvestments = (opportunityId?: string) =>
  api.get<RecordedInvestment[]>(
    `/admin/investments${opportunityId ? `?opportunityId=${opportunityId}` : ''}`);

/**
 * Who may be recorded against a raise.
 *
 * For a 506(b) offering the engine returns only investors whose relationship predates
 * it. The picker shows exactly this list rather than every approved investor, so an admin
 * cannot record a closing against someone the offering could never legally have been
 * shown to.
 */
export const listRecordableInvestors = (opportunityId: string) =>
  api.get<RecordableInvestor[]>(`/admin/opportunities/${opportunityId}/investors`);

export interface RecordInvestmentInput {
  opportunityId: string;
  investorUserId: string;
  /** The only figure the admin supplies. Amounts are derived by the engine. */
  shares: number;
  closingNotes?: string | null;
}

export const recordInvestment = (input: RecordInvestmentInput) =>
  api.post<{
    investmentId: string;
    contractNumber: string;
    status: ClosingStatus;
    shares: number;
    amountInvestedCents: number;
    platformEntryFeeCents: number;
  }>('/admin/investments', input);

export const confirmFundsReceived = (investmentId: string, reference?: string) =>
  api.post<{ status: ClosingStatus }>(`/admin/investments/${investmentId}/funds`, { reference });

export const cancelInvestment = (investmentId: string, reason?: string) =>
  api.post<{ status: ClosingStatus; sharesReleased: number }>(
    `/admin/investments/${investmentId}/cancel`, { reason });

// ── Investor side ───────────────────────────────────────────────────────────

/**
 * The investor signs. Runs under their own session, and the engine checks ownership,
 * so this cannot be used to sign on anyone else's behalf.
 */
export const countersignInvestment = (investmentId: string, signature: string) =>
  api.post<{ status: ClosingStatus; contractNumber: string; signedAt: string }>(
    `/investments/${investmentId}/countersign`, { signature });

export interface MyInvestment {
  id: string;
  contractNumber: string;
  eventTitle: string;
  eventDate: string | null;
  shares: number;
  amountInvestedCents: number;
  /** Per unit. Not the same figure as the entry fee, which is 2.5% of the whole. */
  pricePerShareCents: number;
  platformEntryFeeCents: number;
  achPaymentStatus: string | null;
  status: ClosingStatus;
  createdAt: string;
}

export const listMyInvestments = () => api.get<MyInvestment[]>('/investments/mine');

export const contractUrl = (investmentId: string) => `/api/investments/${investmentId}/contract`;

// ── Investor ledger (41e) ───────────────────────────────────────────────────

/**
 * The three lines an investor is shown, kept apart in the type as they are on screen.
 *
 * There is deliberately no combined total. Invested capital is a contractual fact, the
 * estimated position is a projection off unsettled ticket sales, and distributed is money
 * that has actually moved. A single field summing them would be a number no one could
 * defend, so the shape does not offer one.
 */
/**
 * The stages the estimate is built from, in the order money actually moves.
 *
 * Two halves, split by a real bank transfer. Everything down to netProceedsCents happens
 * on Voleto's books; everything below it happens inside the SPV after the money has left.
 */
export interface PositionBasis {
  grossTicketRevenueCents: number;
  completedOrders: number;
  stripeProcessingFeeCents: number;
  /** The 2.9% half. */
  stripePercentageFeeCents: number;
  /** The fixed 30 cents per order half. */
  stripeFixedFeeCents: number;
  voletoPlatformFeeCents: number;
  /**
   * The venue's negotiated cut of gross, where this event carries one, and the rate it was
   * taken at. Deducted on Voleto's books alongside the fees above, ABOVE the investor
   * waterfall: the venue is an operating cost of the location, not a participant in the
   * split, so it reduces the pool rather than taking a share of it.
   *
   * Both are zero for nearly every event, and the engine sends zero rather than omitting
   * them so a screen never has to guess whether an absent field means no arrangement.
   */
  venueRevenueShareCents: number;
  venueRevenueShareRate: number;
  /** What transfers to the SPV. 100% of it. */
  netProceedsCents: number;
  /**
   * Expenses the RAISE DID NOT already pay: settlement costs, taxes, post-event expenses.
   * The funded production budget is deliberately absent, because the raise IS that budget
   * and return of capital is where it is accounted for.
   */
  unfundedExpensesCents: number;
  /** What enters the waterfall. */
  netEventCashCents: number;
  /** What the SPV has left to run the waterfall over, across the whole event. */
  distributableCashCents: number;
  /** This raise's share of it. Equal to the above unless the event carries several. */
  trancheShareCents: number;
  /** The carry rate on THIS offering, not the platform default. */
  gpCarryRate: number;
  /** Capital across every raise on the event, for context only. */
  eventTotalInvestedCapitalCents: number;

  // The three tiers, at the pool level, in the order they are paid.
  preferredReturnRate: number;
  /** Tier 1: capital returned to investors before anyone takes a profit share. */
  poolReturnOfCapitalCents: number;
  /** Tier 2: the hurdle actually paid, which may be less than the full target. */
  poolPreferredReturnCents: number;
  poolPreferredTargetCents: number;
  /** Tier 3: only this is split. */
  profitAboveHurdleCents: number;
  /** 60% of the profit above the hurdle. */
  investorPoolCents: number;
  /** The 40% carried interest, to the manager. */
  gpFeeCents: number;
  /** Legacy alias the engine still emits. Same value; prefer gpFeeCents. */
  gpPromoteCents: number;

  totalInvestedCapitalCents: number;
  capitalReturned: boolean;
  sharesHeld: number;
  sharesOutstanding: number;
}

export interface EstimatedPosition {
  /** False once the event has settled: then this is the figure settlement used. */
  estimated: boolean;
  settled: boolean;
  asOf: string;
  basis: PositionBasis;
  /** This investor's own slice, tier by tier. */
  returnOfCapitalCents: number;
  preferredReturnCents: number;
  profitShareCents: number;
  /** What they keep beyond their own principal. Can be negative. */
  profitCents: number;
  positionCents: number;
}

export interface SaleActivity {
  id: string;
  netCents: number;
  grossCents: number;
  refundedCents: number;
  ticketCount: number;
  status: string;
  at: string;
}

/** Whether net proceeds have actually left Voleto for the SPV. */
export interface NetProceedsTransfer {
  status: 'pending' | 'processing' | 'paid' | 'reversed' | 'failed';
  transferred: boolean;
  amountCents: number;
  transferredAt: string | null;
}

/** One ticket tier on the event, with what has actually sold. Counts only, no buyer data. */
export interface LedgerTicketTier {
  name: string;
  priceCents: number;
  capacity: number;
  sold: number;
  remaining: number;
  /** External identifier from another system, for reconciliation. Usually null. */
  variationRef: string | null;
}

export interface Ledger {
  id: string;
  contractNumber: string;
  status: ClosingStatus;
  isActive: boolean;
  netProceedsTransfer: NetProceedsTransfer | null;
  hasContract: boolean;
  settled: boolean;
  event: {
    id: string; title: string; date: string | null;
    venueName: string | null; venueCity: string | null;
  };
  opportunity: { id: string; title: string; offeringType: string };
  /** The entity holding the money. One SPV can back several tranches. */
  spv: { legalEntityName: string } | null;
  investedCapital: {
    amountCents: number;
    shares: number;
    pricePerShareCents: number;
    entryFeeCents: number;
    recordedAt: string;
    signedAt: string | null;
    fundsConfirmedAt: string | null;
  };
  estimatedPosition: EstimatedPosition | null;
  distributedToDate: {
    /** Only what an admin has confirmed the SPV actually sent. Never inferred. */
    distributedCents: number;
    payoutCount: number;
    lastDistributedAt: string | null;
    /** Calculated as owed at settlement, but not yet confirmed as sent. */
    awaitingDistributionCents: number;
    awaitingCount: number;
  };
  /**
   * Sold against capacity per tier: the breakdown behind the gross figure the estimate is
   * computed from, so the headline number can be checked rather than taken on trust.
   * Empty for a holding with no position yet.
   */
  ticketTiers: LedgerTicketTier[];
  activity: SaleActivity[];
}

export const getLedger = (investmentId: string) =>
  api.get<Ledger>(`/investments/${investmentId}/ledger`);

/** What the SSE stream pushes on each sale. */
export interface LiveSnapshot {
  status: ClosingStatus;
  isActive: boolean;
  estimatedPosition: EstimatedPosition | null;
  activity?: SaleActivity[];
  /** Pushed with every update, so the bars cannot freeze while the figures move. */
  ticketTiers?: LedgerTicketTier[];
  reason?: string;
}
